<template>
  <div v-if="attachments.length" class="mt-4">
    <!-- Trigger Thumbnail -->
    <div class="flex flex-wrap gap-4 mt-4">
      <div
        v-for="(attachment, index) in attachments"
        :key="index"
        class="cursor-pointer"
        @click="openLightbox(index)"
      >
        <LazyImage
          v-if="isImage(attachment)"
          :image="attachment"
          alt="attachment preview"
        />
        <div v-else class="w-[90px] h-[90px] flex items-center justify-center bg-slate-200 p-4 rounded hover:scale-105 transition-transform">
          {{ fileTypeToString(attachment) }}
        </div>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <div
      v-if="showLightbox"
      ref="lightboxRef"
      class="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
      @click.self="closeLightbox"
    >
      <div class="max-w-4xl w-full p-4">
        <div tabindex="0" class="sr-only">Focus helper</div>
        <!-- Content -->
        <div class="grid grid-cols-[auto_1fr_auto] grid-rows-[auto_1fr] items-stretch gap-4 h-full">
          <!-- 1rst row -->
          <div />
          <div class="flex justify-center items-end">
            <!-- Attachment Index -->
            <p class="text-white text-center self-center">{{ currentIndex + 1 }} / {{ attachments.length }}</p>
          </div>
          <!-- Close Button -->
          <div class="flex justify-end items-end">
            <button
              class="text-white text-3xl"
              @click="closeLightbox"
            >
              &times;
            </button>
          </div>
          <!-- end 1rst row -->

          <!-- 2nd row -->
          <!-- Prev -->
          <div class="flex items-center">
            <button
              :class="{ 'opacity-50 cursor-not-allowed': currentIndex === 0 }"
              :disabled="currentIndex === 0"
              class="text-white text-3xl"
              @click="prev"
            >
              &#10094;
            </button>
          </div>

          <div class="max-h-[80vh] overflow-hidden flex justify-center items-center">
            <Transition :name="direction === 'next' ? 'slide-next' : 'slide-prev'" mode="out-in">
              <div
                :key="currentAttachment"
                class="max-h-[80vh] overflow-auto flex justify-center items-center"
              >
                <img
                  v-if="isImage(currentAttachment)"
                  :src="currentAttachment"
                  class="rounded max-h-[80vh] mx-auto attachment object-contain min-h-[50vh]"
                />

                <div
                  v-else
                  class="h-[50vh] flex items-center justify-center"
                >
                  <a
                    :href="currentAttachment"
                    target="_blank"
                    class="text-lg underline attachment text-white"
                  >{{ getFileName(currentAttachment) }}</a>
                </div>
              </div>
            </Transition>
          </div>

          <!-- Next -->
          <div class="flex items-center">
            <button
              :class="{ 'opacity-50 cursor-not-allowed': currentIndex === attachments.length - 1 }"
              :disabled="currentIndex === attachments.length - 1"
              class="text-white text-3xl"
              @click="next"
            >
              &#10095;
            </button>
          </div>
        </div>

        <!-- Attachment Name -->
        <p class="text-white text-center mt-4">{{ getFileName(currentAttachment) }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeUnmount, watch, useTemplateRef, PropType, nextTick } from 'vue'
import { isImage, fileTypeToString, getFileName } from '@/utils'
import { createFocusTrap } from 'focus-trap'
import LazyImage from '../LazyImage.vue'

const props = defineProps({
  attachments: {
    type: Array as PropType<string[]>,
    required: true,
    default: () => []
  }
})

const direction = ref<'next' | 'prev'>('next')
const showLightbox = ref(false)
const currentIndex = ref(0)

const currentAttachment = computed(() => props.attachments[currentIndex.value] || '')

const openLightbox = (index) => {
  currentIndex.value = index
  showLightbox.value = true
}

const closeLightbox = () => {
  showLightbox.value = false
}

const next = () => {
  if (currentIndex.value < props.attachments.length - 1) {
    currentIndex.value++
    direction.value = 'next'
  }
}

const prev = () => {
  if (currentIndex.value > 0) {
    currentIndex.value--
    direction.value = 'prev'
  }
}

let focusTrap
const lightboxRef = useTemplateRef('lightboxRef')

watch(showLightbox, async (value) => {
  if (value) {
    await nextTick()
    focusTrap = createFocusTrap(lightboxRef.value, { escapeDeactivates: false })
    focusTrap.activate()
    document.body.classList.add('overflow-hidden')
  } else {
    document.body.classList.remove('overflow-hidden')
    focusTrap?.deactivate()
  }
})

onBeforeUnmount(() => {
  focusTrap?.deactivate()
})
</script>

<style scoped>
.attachment {
  transition: transform 0.3s ease;
}
.slide-next-enter-active,
.slide-next-leave-active,
.slide-prev-enter-active,
.slide-prev-leave-active {
  transition: transform 0.4s ease, opacity 0.4s ease;
  overflow: hidden;
}

/* Next slide => de droite vers gauche */
.slide-next-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.slide-next-enter-to {
  transform: translateX(0);
  opacity: 1;
}
.slide-next-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.slide-next-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Prev slide => de gauche vers droite */
.slide-prev-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-prev-enter-to {
  transform: translateX(0);
  opacity: 1;
}
.slide-prev-leave-from {
  transform: translateX(0);
  opacity: 1;
}
.slide-prev-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

</style>
